import { Badge, Box, Button, toast, type BadgeVariant } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useState } from 'react';

import {
  AddendumType,
  CANCELLABLE_CHANGE_ORDER_STATUSES,
  CHANGE_ORDER_REASON_LABELS,
  ChangeOrderDocument,
  ChangeOrderStatus,
  Quote,
  RESENDABLE_CHANGE_ORDER_STATUSES,
  UserPermissions,
} from 'types';

import { useCancelChangeOrderMutation, useResendChangeOrderMutation } from '../../services/changeOrders';
import { useGetDocumentByAccountAndDocumentIdQuery } from '../../services/documents';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import RequirePermissions from '../auth/requirePermissions';
import DateTimeDisplay from '../DateTime';
import { FileItem } from '../documents/FileItem';

import { CancelChangeOrderModal } from './CancelChangeOrderModal';

// Status → badge label/variant. `created`/`sent` read as "Sent" to the admin (a freshly created CO
// has already had its offer links generated). Terminal states keep their own labels so the history
// row stays meaningful after resolution.
const STATUS_BADGE: Record<ChangeOrderStatus, { label: string; variant: BadgeVariant }> = {
  [ChangeOrderStatus.created]: { label: 'Sent', variant: 'info' },
  [ChangeOrderStatus.sent]: { label: 'Sent', variant: 'info' },
  [ChangeOrderStatus.viewed]: { label: 'Viewed', variant: 'info' },
  [ChangeOrderStatus.awaitingDisclosure]: { label: 'Awaiting Disclosure', variant: 'info' },
  [ChangeOrderStatus.partiallyAccepted]: { label: 'Partial Acceptance', variant: 'primary' },
  [ChangeOrderStatus.executed]: { label: 'Executed', variant: 'success' },
  [ChangeOrderStatus.declined]: { label: 'Declined', variant: 'danger' },
  [ChangeOrderStatus.expired]: { label: 'Expired', variant: 'warning' },
  [ChangeOrderStatus.cancelled]: { label: 'Cancelled', variant: 'warning' },
  [ChangeOrderStatus.voided]: { label: 'VOIDED', variant: 'warning' },
};

// Only the addendum types that exist today. LightReach-admin recontract labels land later, so this
// map is intentionally incomplete and callers fall back to a generic label.
const ADDENDUM_TYPE_LABEL: Partial<Record<AddendumType, string>> = {
  [AddendumType.epcChangeOrder]: 'Change Order',
};

const CHANGE_ORDER_MANAGE_PERMISSIONS = [
  UserPermissions.admin,
  UserPermissions.editor,
  UserPermissions.lightReachSupport,
];

const CANCEL_REJECTED_MESSAGE = 'This change order can no longer be cancelled. Please review its current status';

const RESEND_REJECTED_MESSAGE = 'This change order can no longer be resent. Please review its current status';
const RESEND_IN_PROGRESS_MESSAGE = 'A resend for this change order is already in progress';
const RESEND_UNDELIVERED_MESSAGE = 'The change order could not be delivered. Please try again';

/**
 * A history row for one addendum (change order) under its parent contract, connected and indented
 * beneath it. Renders for ALL addenda — including terminal ones — so the full chain stays visible.
 * Voided addenda are grayed out with their quote figures struck through; declined addenda surface
 * the decline reason. The amendment PDF link renders only once `amendmentDocumentId` is populated
 * (FIN-4728) and resolves to a document.
 *
 * A row whose own status is still cancellable offers a Cancel action — decided per row, so terminal
 * rows in the same list never show it.
 */
export function ChangeOrderContractRow({
  changeOrder,
  quote,
  indentLeft,
  accountId,
}: {
  changeOrder: ChangeOrderDocument;
  quote?: Quote;
  indentLeft?: number;
  accountId: string;
}): React.JSX.Element {
  const isVoided = changeOrder.status === ChangeOrderStatus.voided;
  const badge = STATUS_BADGE[changeOrder.status];
  const typeLabel = ADDENDUM_TYPE_LABEL[changeOrder.type] ?? 'Change Order';
  // The most recent decline reason (a single decline is terminal, so this is normally the only one).
  const declineReason = changeOrder.declines?.[changeOrder.declines.length - 1]?.reason;
  const initiatingParty = changeOrder.initiatedBy?.userName;
  const epcName = changeOrder.initiatedBy?.organizationName;

  const { easyChangeOrder = false } = useFlags();
  const allowsAction = useIsOrgReadOnly();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelChangeOrder, { isLoading: isCancelling }] = useCancelChangeOrderMutation();
  const isCancellable = CANCELLABLE_CHANGE_ORDER_STATUSES.includes(changeOrder.status);

  const [resendChangeOrder, { isLoading: isResending }] = useResendChangeOrderMutation();
  const isResendable = RESENDABLE_CHANGE_ORDER_STATUSES.has(changeOrder.status);

  const openCancelModal = useCallback(() => setIsCancelModalOpen(true), []);
  const closeCancelModal = useCallback(() => setIsCancelModalOpen(false), []);

  const handleResend = useCallback(async () => {
    const toastId = toast.loading('Resending change order');
    try {
      await resendChangeOrder({ accountId, changeOrderId: String(changeOrder.id) }).unwrap();
      toast.success('Change order resent');
    } catch (e: any) {
      if (e?.status === 400) {
        toast.error(RESEND_REJECTED_MESSAGE);
      } else if (e?.status === 409) {
        toast.error(RESEND_IN_PROGRESS_MESSAGE);
      } else if (e?.status === 503) {
        toast.error(RESEND_UNDELIVERED_MESSAGE);
      } else {
        toast.error(e?.data?.message || 'Error resending change order');
      }
    } finally {
      toast.dismiss(toastId);
    }
  }, [resendChangeOrder, accountId, changeOrder.id]);

  const handleCancel = useCallback(async () => {
    const toastId = toast.loading('Cancelling change order');
    try {
      await cancelChangeOrder({ accountId, changeOrderId: String(changeOrder.id) }).unwrap();
      toast.success('Change order cancelled');
    } catch (e: any) {
      toast.error(e?.status === 400 ? CANCEL_REJECTED_MESSAGE : e?.data?.message || 'Error cancelling change order');
    } finally {
      toast.dismiss(toastId);
      setIsCancelModalOpen(false);
    }
  }, [cancelChangeOrder, accountId, changeOrder.id]);

  const amendmentDocumentId = changeOrder.amendmentDocumentId ? String(changeOrder.amendmentDocumentId) : undefined;
  const { data: amendmentDocument } = useGetDocumentByAccountAndDocumentIdQuery(
    { accountId, documentId: amendmentDocumentId as string },
    { skip: !accountId || !amendmentDocumentId },
  );
  const amendmentFile = amendmentDocument?.files?.[0];

  const struckStyle = isVoided ? { textDecoration: 'line-through' as const } : undefined;

  return (
    <Box
      borderWidth="xs 0 0 0"
      borderColor="separator"
      padding="md lg"
      direction="row"
      childGap="lg"
      style={isVoided ? { opacity: 0.6 } : undefined}
    >
      <Box width="xl" margin={indentLeft ? '0 0 0 2xl' : undefined}>
        <Badge style={{ width: 'fit-content' }} message={badge.label} variant={badge.variant} />
      </Box>
      <Box flex="auto" childGap="xs">
        <Box fontWeight="medium" style={struckStyle}>
          {quote?.productName ? `${typeLabel} - ${quote.productName}` : typeLabel}
        </Box>
        {quote && (
          <Box direction="row" childGap="md" fontSize="xs" color="body-secondary" style={struckStyle}>
            {quote.systemSizeKw != null && <Box>{`${quote.systemSizeKw} kW`}</Box>}
            {quote.kwhPrice != null && <Box>{`$${quote.kwhPrice}/kWh`}</Box>}
            {quote.escalationRate != null && <Box>{`${quote.escalationRate}% escalator`}</Box>}
          </Box>
        )}
        <Box direction="row" childGap="md" fontSize="xs" color="body-secondary">
          {epcName && <Box>{epcName}</Box>}
          {initiatingParty && <Box>{`Initiated by ${initiatingParty}`}</Box>}
          {changeOrder.initiatedBy?.submittedAt && (
            <DateTimeDisplay value={changeOrder.initiatedBy.submittedAt} fontSize="xs" dateFormat="DD" inline />
          )}
        </Box>
        {changeOrder.reasons?.length > 0 && (
          <Box fontSize="xs" color="body-secondary">
            {`Reason${changeOrder.reasons.length > 1 ? 's' : ''}: `}
            {changeOrder.reasons.map((reason) => CHANGE_ORDER_REASON_LABELS[reason]).join(', ')}
          </Box>
        )}
        {declineReason && (
          <Box fontSize="xs" color="danger">
            {`Declined: ${declineReason}`}
          </Box>
        )}
        {amendmentFile && <FileItem file={amendmentFile} />}
      </Box>
      {easyChangeOrder && (isResendable || isCancellable) && (
        <Box direction="row" gap="sm" alignSelf="flex-start" style={{ whiteSpace: 'nowrap' as const }}>
          <RequirePermissions permissions={CHANGE_ORDER_MANAGE_PERMISSIONS} checkAllPermissions={false}>
            {isResendable && (
              <Button
                size="sm"
                variant="secondary"
                tone="neutral"
                isDisabled={isResending || !allowsAction}
                onClick={handleResend}
              >
                Resend
              </Button>
            )}
            {isCancellable && (
              <Button
                size="sm"
                variant="secondary"
                tone="neutral"
                isDisabled={isCancelling || !allowsAction}
                onClick={openCancelModal}
              >
                Cancel
              </Button>
            )}
          </RequirePermissions>
          {isCancellable && (
            <CancelChangeOrderModal
              isOpen={isCancelModalOpen}
              isCancelling={isCancelling}
              onDismiss={closeCancelModal}
              onConfirm={handleCancel}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
